/**
 * Community window: global chat channels over the social endpoints.
 *
 * Messages are plain text and rendered as text nodes only — user-generated
 * content is never interpreted as HTML. Polling stands in for the realtime
 * gateway, which is not built yet.
 */

import type { ChatMessage } from "@parcelbound/contracts";
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { api, ApiError } from "./api";
import { joinRoom, onRealtime } from "./realtime";
import { useApp } from "./store";
import { Window } from "./Window";

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "something went wrong";
}

export function CommunityWindow(): JSX.Element {
  const focused = useApp((s) => s.focused) === "community";
  const closeWindow = useApp((s) => s.closeWindow);
  const focusWindow = useApp((s) => s.focusWindow);
  const session = useApp((s) => s.session);
  const notify = useApp((s) => s.notify);

  const [channels, setChannels] = useState<Array<{ id: string; name: string; kind: string }>>([]);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);

  const loadChannels = (): void => {
    api.channels().then(
      (r) => {
        setChannels(r.channels.map((c) => ({ id: c.id, name: c.name, kind: c.kind })));
        setChannelId((current) => current ?? r.channels[0]?.id ?? null);
      },
      (e) => notify("err", describe(e)),
    );
  };
  useEffect(loadChannels, []);

  /** Block, report or message another player, from their name in the log. */
  const authorActions = async (m: ChatMessage): Promise<void> => {
    if (!m.authorId || m.authorId === session?.id) return;
    const choice = window.prompt(
      `${m.authorName}: type "dm" to message them, "block" to hide them, or "report <reason>"`,
      "dm",
    );
    if (!choice) return;
    try {
      if (choice === "dm") {
        const channel = await api.openDm(m.authorId);
        loadChannels();
        setChannelId(channel.id);
        notify("ok", `Direct channel with ${m.authorName} opened.`);
      } else if (choice === "block") {
        await api.blockPlayer(m.authorId);
        notify("ok", `${m.authorName} blocked; their messages are gone for you.`);
        const r = await api.messages(channelId!);
        setMessages(r.messages);
      } else if (choice.startsWith("report")) {
        const reason = choice.slice(6).trim() || "reported from chat";
        await api.reportPlayer(m.authorId, `chat message ${m.id}`, reason);
        notify("ok", "Report filed for the moderation queue.");
      }
    } catch (err) {
      notify("err", describe(err));
    }
  };

  useEffect(() => {
    if (!channelId) return;
    let live = true;
    const load = (): void => {
      api.messages(channelId).then(
        (r) => {
          if (live) setMessages(r.messages);
        },
        () => undefined,
      );
    };
    load();
    // Pushed over the realtime socket; the slow poll is only the fallback.
    const leave = joinRoom(`chat:${channelId}`);
    const off = onRealtime((event) => {
      if (event.type === "chat.message" && event.payload.channelId === channelId) load();
    });
    const t = setInterval(load, 30_000);
    return () => {
      live = false;
      clearInterval(t);
      leave();
      off();
    };
  }, [channelId]);

  // Follow the newest message unless the reader has scrolled up.
  useEffect(() => {
    const el = logRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async (): Promise<void> => {
    const body = draft.trim();
    if (!channelId || body.length === 0) return;
    setBusy(true);
    try {
      await api.postMessage(channelId, body);
      setDraft("");
      const r = await api.messages(channelId);
      setMessages(r.messages);
    } catch (err) {
      notify("err", describe(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Window
      title="Community"
      initial={{ x: Math.max(16, window.innerWidth - 460), y: 140 }}
      width={420}
      maxHeight={560}
      focused={focused}
      onClose={() => closeWindow("community")}
      onFocus={() => focusWindow("community")}
      tabs={
        <div className="tabs" role="tablist">
          {channels.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={channelId === c.id}
              onClick={() => setChannelId(c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      }
    >
      <div
        ref={logRef}
        className="chat-log"
        style={{ maxHeight: 330, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
      >
        {messages.length === 0 ? (
          <p className="empty">Nothing here yet. Say hello.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="small" style={{ lineHeight: 1.45 }}>
              <button
                className={m.authorId === session?.id ? "up" : "faint"}
                style={{
                  fontWeight: 700,
                  marginRight: 6,
                  background: "none",
                  border: 0,
                  padding: 0,
                  cursor: m.authorId && m.authorId !== session?.id ? "pointer" : "default",
                  font: "inherit",
                }}
                title={
                  m.authorId && m.authorId !== session?.id
                    ? "Message, block or report this player"
                    : undefined
                }
                onClick={() => void authorActions(m)}
              >
                {m.authorName}
              </button>
              <span className="faint" style={{ marginRight: 6, fontSize: 11 }}>
                {new Date(m.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
              <span style={{ wordBreak: "break-word" }}>{m.body}</span>
            </div>
          ))
        )}
      </div>

      <form
        style={{ display: "flex", gap: 8, marginTop: 10 }}
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          style={{ flex: 1 }}
          value={draft}
          maxLength={500}
          placeholder="Message…"
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Chat message"
        />
        <button className="primary" type="submit" disabled={busy || draft.trim().length === 0}>
          Send
        </button>
      </form>
    </Window>
  );
}
